import * as moment from 'moment'
import * as React from 'react'
import 'react-dates/initialize'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { withHtmlTag } from '../../../../utils'
import { Button, Score } from '../../../atoms'
import { ReviewListModalBody } from 'components/molecules'
import { ReviewCard } from 'components/molecules/ReviewCard'
import { checkLikeable } from 'utils'
import TextClamp from 'components/atoms/TextClamp'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import { PostStickyBox } from './StickyBox'
import { TravelerPhotos } from './TravelerPhotos'
import injectGoogleMaps from 'utils/injectGoogleMaps'
import { PostDetailImages } from './PostDetailImages'
import { PostTop } from './PostTop'
import {
  getDatesFromSearchFilters,
  getSearchFiltersCookie,
  updateSearchFiltersCookie,
} from 'utils/searchFiltersCookie'
import SvgIcon from 'components/atoms/SvgIcon'
import { useBodyClassForMediaQuery } from 'utils/hooks'

import { PostIncluded } from './PostIncluded'
import { PostIncludedModalBody } from './PostIncludedModalBody'
import { buildReservationUrl } from 'utils/url'
import { setReservationStorageData } from 'utils/reservationSessionStorage'
import { RelatedPosts } from './RelatedPosts'

declare var window: interfaces.IWindow

interface IProps {
  editable: boolean
  google: any
  isOwner: boolean
  isSignedIn: boolean
  post_owner: interfaces.IUser
  post: any
  related_posts: any
  reviews: any
  identification_required: boolean
  profile_required: boolean
  image_url?: string
  selectedDate: moment.Moment | null
  totalPrice: null | number
  user: interfaces.IUser
  cancel_policy: string
  member_posts: any
  highlightedReviews: any
  setTotalPrice(totalPrice: number): void
  setSelectedDate(selectedDate: moment.Moment | null): void
}

const MIN_TEXT_LENGTH_MOBILE = 120

// child components
const PostShowText: React.FC<{
  isMobile: boolean
  content?: string
  showAlways?: boolean
}> = props => {
  const [showAll, setShowAll] = React.useState(!!props.showAlways)

  const needShowMore = React.useMemo(() => {
    return !props.showAlways && props.isMobile && props.content?.length > MIN_TEXT_LENGTH_MOBILE
  }, [props])

  const formattedContent = React.useMemo(
    () => (
      <div className="with-icon" dangerouslySetInnerHTML={{ __html: withHtmlTag(props.content) }} />
    ),
    [props]
  )

  return (
    <div className="PostShow_Text">
      {showAll || !needShowMore ? (
        formattedContent
      ) : (
        <TextClamp line={3}>{formattedContent}</TextClamp>
      )}

      {needShowMore && (
        <ShowMore>
          <a onClick={() => setShowAll(old => !old)}>
            {showAll ? I18n.t('generic.close') : I18n.t('generic.show_more')}
          </a>
        </ShowMore>
      )}
    </div>
  )
}

// main component
export const PostDetail: React.FC<IProps> = props => {
  const searchFiltersCookie = getSearchFiltersCookie()
  const urlParams = new URLSearchParams(window.location.search)
  const initDate = searchFiltersCookie?.date ?? null
  const initGuests = searchFiltersCookie
    ? JSON.stringify([
        searchFiltersCookie.guests.adults,
        searchFiltersCookie.guests.children,
        searchFiltersCookie.guests.infants,
      ])
    : null
  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_SMALL}px)`).matches
  const initialReviews = props.reviews.data
  const [reviews, setReviews] = React.useState([...initialReviews])
  const [map, setMap] = React.useState(null)

  const initializeMap = () => {
    const meetingPoint = props.post.meeting_points[0] || { latitude: null, longitude: null }
    const { latitude, longitude } = meetingPoint

    if (!latitude || !longitude) {
      return
    }

    const LatLng = new props.google.maps.LatLng(latitude, longitude)
    const Options = {
      zoom: 15,
      center: LatLng,
      mapTypeId: 'roadmap',
      scrollwheel: false,
      draggable: false,
    }
    const initializedMap = new props.google.maps.Map(document.getElementById('map'), Options)
    new props.google.maps.Marker({
      position: LatLng,
      title: props.post.name,
      map: initializedMap,
    })

    setMap(initializedMap)
  }

  React.useEffect(() => {
    document.body.classList.add('detail')

    return () => {
      document.body.classList.remove('detail')
    }
  }, [])

  useBodyClassForMediaQuery(
    constants.HAS_FIXED_FOOTER_CLASS,
    `(max-width: ${constants.BREAKPOINT_TABLET_LARGE}px)`
  )

  React.useEffect(() => {
    if (props.google) {
      initializeMap()
    }
  }, [props.google])

  const likeable = React.useMemo(() => {
    return checkLikeable(props.post, props.user)
  }, [props.post, props.user])

  const showReviewListModal = () => {
    window.globalModal.showModal({
      body: (
        <ReviewListModalBody
          searchParams={{ post_ids: [props.post.id] }}
          score={props.post.average_review_score}
          count={props.post.reviews_count}
        />
      ),
    })
  }

  const showIncludedModal = () => {
    window.globalModal.showModal({
      body: <PostIncludedModalBody includedItems={props.post.post_detail?.included_items ?? []} />,
    })
  }

  const showIncludedSection = props.post.post_detail?.included_items?.length > 0

  const handleClickCheckAvailability = () => {
    const initParam = {
      date: getDatesFromSearchFilters(searchFiltersCookie),
      guests: initGuests ? JSON.parse(initGuests) : null,
    }

    const initialGuests = {
      adults: initParam.guests?.[0] ?? 1,
      children: initParam.guests?.[1] ?? 0,
      infants: initParam.guests?.[2] ?? 0,
    }

    setReservationStorageData({
      initParams: {
        dates: initParam.date.map(date => date.toISOString()),
        guests: initialGuests,
      },
    })
    updateSearchFiltersCookie({
      ...(initDate ? { date: initDate } : {}),
      guests: initialGuests,
    })

    const guestCount = initialGuests.adults + initialGuests.children
    window.location.href = buildReservationUrl(
      props.post,
      initParam.date,
      guestCount,
      initialGuests
    )
  }

  // user.id === props.post.user.id <- use in showSidebar to show sidebar for guides/agent only
  return (
    <SidebarLayout user={props.user} showSidebar={false}>
      <PostShowWrapper className="PostShow">
        <div className="PostShow_Main">
          <div className="PostShow_Content_Top">
            <PostTop editable={props.editable} post={props.post} likeable={likeable} />
            <PostDetailImages post={props.post} likeable={likeable} />
          </div>
          <div className="PostShow_Content">
            <PostStickyBox {...props} />
            <div className="PostShow_Inner">
              <div className="PostShow_Section Overview">
                <PostShowText isMobile={isMobile} content={props.post.post_detail?.overview} />
                <ul>
                  <li>
                    <SvgIcon
                      name="timelapse"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    <span>
                      {props.post.duration} {I18n.t('generic.hours')}
                    </span>
                  </li>
                  <li className="lang">
                    <SvgIcon
                      name="language"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    <div className="offered">
                      {I18n.t('generic.offered_in')}&nbsp;
                      <span>
                        {props.post.languages
                          .map(language => language.name_i18n)
                          .join(', ')
                          .replace(/(, )(?!.*\1)/, ' and ')}
                      </span>
                    </div>
                  </li>
                  {/* インスタントブッキングがデザインにないけどとりあえずそのまま*/}
                  {/* {props.post.instant_booking && (
                    <li>
                      <SvgIcon name="bolt" className="icon instant-booking" />
                      <span>{I18n.t('post.instant_booking')}</span>
                    </li>
                  )}*/}
                </ul>
              </div>
              {props.post.post_detail?.highlight && (
                <div className="PostShow_Section">
                  <h2>{I18n.t('post.highlight')}</h2>
                  <PostShowText
                    isMobile={isMobile}
                    content={props.post.post_detail?.highlight}
                    showAlways={true}
                  />
                </div>
              )}
              {props.highlightedReviews.length > 0 && (
                <div className="PostShow_Section">
                  <h2>{I18n.t('post.highlighted_reviews')}</h2>
                  <div className="PostShow_HighLightedReviews">
                    {props.highlightedReviews.map(review => (
                      <div className="PostShow_HighLightedReview" key={review.id}>
                        <ReviewCard review={review} />
                      </div>
                    ))}
                  </div>
                  <div className="ShowAll">
                    <button type="button" onClick={showReviewListModal}>
                      {I18n.t('generic.show_all_reviews')}
                    </button>
                  </div>
                </div>
              )}

              {showIncludedSection && (
                <div className="PostShow_Section">
                  <div className="included-header">
                    <h2>{I18n.t('post.included')}</h2>
                    <div className="show-more" role="button" onClick={showIncludedModal}>
                      {I18n.t('generic.show_more')}
                    </div>
                  </div>
                  <div className="included-body">
                    <PostIncluded
                      includedItems={props.post.post_detail?.included_items ?? []}
                      showMoreHandler={showIncludedModal}
                    />
                  </div>
                </div>
              )}
              {/* 過去互換表示 */}
              {!showIncludedSection && props.post.post_detail?.included && (
                <div className="PostShow_Section">
                  <h2>{I18n.t('post.included')}</h2>
                  <PostShowText isMobile={isMobile} content={props.post.post_detail.included} />
                </div>
              )}

              {props.post.post_videos?.length > 0 && (
                <section className="PostShow_Section">
                  <VideoList>
                    {props.post.post_videos.map(postVideo => (
                      <li className="VideoList_Item" key={postVideo.id}>
                        <h2 className="name">{postVideo.name}</h2>
                        {postVideo.media === 'youtube' ? (
                          <div className="video">
                            <iframe
                              key={postVideo.id}
                              width="426"
                              height="240"
                              src={`https://www.youtube.com/embed/${postVideo.youtube_video_id}`}
                              frameBorder="0"
                            />
                          </div>
                        ) : (
                          <div className="image">
                            <img src={postVideo.image_url} />
                          </div>
                        )}
                        <div className="description">
                          <PostShowText
                            isMobile={isMobile}
                            content={postVideo.description}
                            showAlways={true}
                          />
                        </div>
                      </li>
                    ))}
                  </VideoList>
                </section>
              )}

              <TravelerPhotos post={props.post} />

              <div className="PostShow_Section">
                <h2>{I18n.t('post.itinerary')}</h2>
                <PostShowText isMobile={isMobile} content={props.post.post_detail?.itinerary} />
                {props.post.meeting_points && props.post.meeting_points[0] && (
                  <>
                    <div className="MeetingPoint_Content">
                      <h3 className="MeetingPoint_Title">
                        <span id="mp_anchor" />
                        {I18n.t('generic.meeting_point')}
                      </h3>
                      <div className="MeetingPoint_Inner">
                        {props.post.meeting_points[0]?.name && (
                          <p className="MeetingPoint_Name">{props.post.meeting_points[0]?.name}</p>
                        )}
                        {props.post.meeting_points[0]?.location && (
                          <p>{props.post.meeting_points[0]?.location}</p>
                        )}
                        {props.post.meeting_points[0]?.memo && (
                          <p
                            dangerouslySetInnerHTML={{
                              __html: withHtmlTag(props.post.meeting_points[0]?.memo),
                            }}
                          />
                        )}
                        {props.post.meeting_points[0]?.latitude &&
                          props.post.meeting_points[0]?.longitude && (
                            <div id="map" className="PostShow_Map" />
                          )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="PostShow_Section">
                <h2>{I18n.t('post.remarks')}</h2>
                <div className="section-mb">
                  <PostShowText isMobile={isMobile} content={props.post.post_detail?.remarks} />
                </div>
                {props.post.post_detail?.not_included && (
                  <>
                    <div className="NotIncluded">
                      <h3>{I18n.t('post.not_included')}</h3>
                      <PostShowText
                        isMobile={isMobile}
                        content={props.post.post_detail?.not_included}
                      />
                    </div>
                  </>
                )}
                {props.cancel_policy && (
                  <>
                    <h3>{I18n.t('post.cancel_policy')}</h3>
                    <PostShowText isMobile={isMobile} content={props.cancel_policy} />
                  </>
                )}
              </div>

              {reviews.length > 0 && (
                <div className="PostShow_Section" id="review">
                  <div className="PostShow_ReviewScore">
                    <h2>{I18n.t('post.traveler_reviews')}</h2>
                    <Score score={props.post.average_review_score} showRoundedScore={true} />
                    <div className="Reviews_Count">
                      <span>{props.post.reviews_count}</span>
                      {I18n.t('post.reviews')}
                    </div>
                  </div>
                  <ReviewsWrapper>
                    {reviews.map((review: interfaces.IReview, index) => (
                      <ReviewCard key={index} review={review} />
                    ))}
                  </ReviewsWrapper>

                  <div className="ShowAll">
                    <button type="button" onClick={showReviewListModal}>
                      {I18n.t('generic.show_all_reviews')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <RelatedPosts post={props.post} user={props.user} />

        <Footer>
          <div className="Footer_Info">
            <div className="Footer_Price">
              <span className="label">From</span>¥<span className="value">{props.post.price}</span>/
              <span className="unit">{props.post.post_type === 'group' ? 'Person' : 'Group'}</span>
            </div>
            {props.post.last_booked_hours !== null && (
              <div className="LastBookedHours">
                <span className="label">
                  <SvgIcon name="history" className="icon" ariaHidden={true} role="presentation" />
                  {I18n.t('post.last_booked_hours_label')}
                </span>
                <span className="value">
                  {I18n.t('post.last_booked_hours', { hours: props.post.last_booked_hours })}
                </span>
                {I18n.t('post.last_booked_hours_unit')}
              </div>
            )}
          </div>
          <div className="Footer_Button">
            <Button primary={true} handleClick={handleClickCheckAvailability}>
              {I18n.t('generic.check_availability')}
            </Button>
          </div>
        </Footer>
      </PostShowWrapper>
    </SidebarLayout>
  )
}

const Footer = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6.4rem;
  background-color: ${constants.COLORS.white};
  box-shadow: ${constants.COLORS.cardShadow};
  z-index: 100;
  @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    display: flex;
    padding: 0.8rem 5rem;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 0.8rem 1.6rem;
  }
  .Footer_Info {
  }
  .Footer_Price {
    font-size: 1.6rem;
    line-height: 2.4rem;
    .label {
      font-size: 1.4rem;
      font-weight: 700;
      margin-right: 0.4rem;
    }
    .value {
      font-size: 1.8rem;
      font-weight: 700;
      margin-right: 0.4rem;
    }
    .unit {
      font-size: 1.2rem;
      margin-left: 0.4rem;
    }
  }
  .LastBookedHours {
    display: flex;
    align-items: center;
    line-height: 2rem;
    font-size: 1.2rem;
    letter-spacing: 0;
    .label {
      display: flex;
      align-items: center;
      margin-right: 0.2rem;
      color: ${constants.COLORS.primary};
      font-weight: 700;
      .icon {
        width: 1.6rem;
        height: 1.6rem;
        margin-right: 0.2rem;
      }
    }
    .value {
      font-size: 1.2rem;
      font-weight: 700;
      margin-right: 0.2rem;
    }
  }
  .Footer_Button {
    button {
      height: auto;
      padding: 0.8rem 1.2rem;
      line-height: 2.4rem;
      font-size: 1.3rem;
      border-radius: 1.2rem;
    }
  }
`

const VideoList = styled.ul`
  list-style: none;

  .VideoList_Item + .VideoList_Item {
    margin-top: 3.2rem;
  }

  .name {
  }

  .description {
  }

  .video {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;

    > iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .image {
    width: 100%;
    img {
      object-fit: cover;
      width: 100%;
    }
  }
`

const ReviewsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`

const ShowMore = styled.div`
  color: ${constants.COLORS.primary};
  cursor: pointer;
  margin-top: 0.8rem;
  a {
    display: flex;
    align-items: center;
    line-height: 3.2rem;
    font-size: 1.6rem;
    text-transform: capitalize;
    text-decoration: underline;
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        text-decoration: none;
      }
    }
  }
`

const PostShowWrapper = styled.div`
  .ShowAll {
    margin-top: 2.4rem;
    button {
      padding: 0;
      lne-height: 2.4rem;
      color: ${constants.COLORS.primary};
      font-size: 1.6rem;
      text-transform: capitalize;
      text-decoration: underline;
      appearance: none;
      background: transparent;
      border: 0;
      cursor: pointer;
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          text-decoration: none;
        }
      }
    }
  }
  .Overview {
    .PostShow_Text {
      font-size: 1.8rem;
      line-height: 2.8rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }
    ul {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 2.4rem;
      margin-top: 1.6rem;
    }

    li {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      color: ${constants.COLORS.black};
      font-size: 1.6rem;
      line-height: 2.4rem;
      &.lang {
        flex: 1 1 auto;
      }
      &:last-child {
        margin-right: 0;
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        width: 100%;
        align-items: flex-start;
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      }

      .icon {
        flex: 0 0 auto;
        color: ${constants.COLORS.subText};
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 0.8rem;
      }

      span {
        flex: 0 0 auto;
        white-space: nowrap;
      }

      .offered {
        span {
          white-space: pre-line;
        }
      }

      .instant-booking {
        color: ${constants.COLORS.useally};
      }
    }
  }

  .MeetingPoint_Content {
    margin-top: 3.2rem;
    .MeetingPoint_Inner {
      margin-top: 1.6rem;
      padding: 1.6rem;
      background-color: ${constants.COLORS.successBG2};
      border-radius: 1.2rem;
    }
    p {
      font-size: 1.4rem;
      line-height: 2rem;
      margin-bottom: 0.4rem;
      &.MeetingPoint_Name {
        color: ${constants.COLORS.primary};
        font-weight: 700;
      }
    }
    .PostShow_Map {
      margin-top: 1.6rem;
      height: 36rem;
      border-radius: 0.4rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        height: 32rem;
      }
    }
  }

  .PostShow_Main {
    max-width: 112rem;
    margin: 0 auto 0;
    padding: 0;
    background-color: ${constants.COLORS.white};
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      margin-top: 3.2rem;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_LARGE}px) {
      max-width: none;
      padding: 0 8rem;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      padding: 0 5rem;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding: 0 1.6rem;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      margin-top: 0;
    }
  }

  .PostShow_Content_Top {
    display: flex;
    flex-direction: column;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      flex-direction: column-reverse;
      border-bottom: 1px solid ${constants.COLORS.divider2};
      .PostGallery_Main {
        margin: 0 -1.6rem;
      }
    }
  }

  .PostShow_Content {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    .section-mb {
      margin-bottom: 2.4rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        margin-bottom: 1.6rem;
      }
    }
    h2 {
      line-height: 4.4rem;
      font-size: 3rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        line-height: 3.2rem;
        margin-bottom: 1.6rem;
        font-size: 2rem;
      }
    }
    > h2 {
      margin-bottom: 2.4rem;
    }
    .included-header {
      margin-bottom: 2.4rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        h2 {
          margin-bottom: 0;
        }
      }

      .show-more {
        color: ${constants.COLORS.primary};
        font-size: 1.6rem;
        text-decoration: underline;
        cursor: pointer;
        @media (hover: hover) and (pointer: fine) {
          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
    .included-body {
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        margin: 0 -1.6rem;
      }
    }

    h3 {
      line-height: 2.4rem;
      margin-bottom: 0.8rem;
      font-size: 1.8rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        line-height: 3.2rem;
        margin-bottom: 1.6rem;
        font-size: 2rem;
      }
    }
  }

  .PostShow_Inner {
    max-width: calc(66rem + 8rem);
    width: 66%;
    padding-right: 8rem;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      padding-right: 4rem;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      width: calc(100% - 32rem);
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      width: 100%;
      max-width: unset;
      padding: 0;
    }
    > div:last-child {
      border: 0;
      padding-bottom: 0;
    }
  }

  .PostShow_Section {
    padding-bottom: 4.8rem;
    margin-bottom: 4rem;
    border-bottom: 1px solid ${constants.COLORS.divider2};
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      padding-bottom: 3.2rem;
      margin-bottom: 2.4rem;
    }

    .content {
      flex: 1;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        margin-top: 24px;
      }
    }
    .participation_method {
      width: 240px;
      padding: 16px;
      border: 1px solid ${constants.COLORS.divider};
      box-sizing: border-box;
      border-radius: 6px;

      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        width: 100%;
      }

      .icon {
        color: ${constants.COLORS.primary};
        width: 32px;
        height: 32px;
      }

      h4 {
        display: flex;
        align-items: center;
        color: ${constants.COLORS.infoText};
        font-size: 16px;
        font-style: normal;
        font-weight: bold;
        margin-bottom: 8px;
      }
      p {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
      }
    }
    .PostShow_Text {
      margin-top: 2.4rem;
      line-height: 2.4rem;
      font-size: 1.6rem;
      font-style: normal;
      font-weight: 400;
      color: ${constants.COLORS.black};
      white-space: pre-wrap;
      a {
        color: ${constants.COLORS.primary};
        text-decoration: underline;
        @media (hover: hover) and (pointer: fine) {
          &:hover {
            text-decoration: none;
          }
        }
      }
    }
  }

  .PostShow_HighLightedReviews {
    display: flex;
    gap: 2.4rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      flex-direction: column;
      gap: 1.6rem;
    }
    .PostShow_HighLightedReview {
      width: calc(50% - 1.2rem);
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        width: 100%;
      }
    }
  }

  .PostShow_ReviewScore {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2.4rem;
    .Score_StarsWrapper {
      width: 16rem;
      height: 3.2rem;
    }
    .Stars_Star {
      width: 3.2rem;
      height: 3.2rem;
    }
    span.Stars_Score {
      display: flex;
      align-items: center;
      line-height: 3.2rem;
      margin-left: 1.2rem;
      color: ${constants.COLORS.black};
      font-size: 2.4rem;
    }
    .Reviews_Count {
      margin-top: 0.8rem;
      color: ${constants.COLORS.subText};
      font-size: 1.4rem;
      span {
        font-size: 1.6rem;
        font-weight: 700;
        margin-right: 0.4rem;
      }
    }
  }

  .reviewImages {
    display: flex;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin: 0 -1.6rem;
    }
  }
  .NotIncluded {
    padding: 1.2rem;
    background-color: ${constants.COLORS.background2};
    border-radius: 0.8rem;
    margin-bottom: 2.4rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      margin-bottom: 1.6rem;
    }
    h3 {
      color: ${constants.COLORS.subText};
      font-size: 1.6rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        line-height: 2rem;
        font-size: 1.4rem;
      }
    }
    .PostShow_Text {
      margin-top: 0;
    }
  }
`

export default injectGoogleMaps(PostDetail)
